import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sprout, CheckCircle, Play } from 'lucide-react';
import VideoModal from '../components/VideoModal';
import mediaAssets from '../config/mediaAssets';

const Agriculture = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#EAF6E6] to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <Sprout className="w-16 h-16 text-[var(--accent-primary)] mx-auto mb-6" />
            <h1 className="heading-1 mb-6">Modern farms run on visibility.</h1>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto mb-8">
              Practical intelligence for field operations—monitor, measure, and improve without guesswork.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">Talk to Ferntree Agriculture</Link>
              <Link to="/contact" className="btn-secondary">Talk to an Expert</Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden shadow-2xl group cursor-pointer"
            onClick={() => setIsVideoOpen(true)}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={mediaAssets.solutionImages.agriculture.hero}
              className="w-full h-[500px] object-cover"
            >
              <source src={mediaAssets.videos.agriculture} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[var(--accent-primary)] flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all">
                <Play className="w-8 h-8 text-white fill-white ml-1" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-2 text-center mb-12">What we deliver</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Field monitoring and operational visibility',
              'Drone-enabled workflows where appropriate',
              'Operational reporting and traceability',
              'Dashboards that help teams prioritise what to inspect next',
              'Training and support designed for real adoption'
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-start gap-3 p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg">
                <CheckCircle className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-body)]">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-[var(--bg-section)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-2 text-center mb-12">Where it fits best</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              'Large farms and plantation-scale operations',
              'Agro-enterprises that manage multiple locations',
              'Teams that need consistent reporting and execution'
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg text-center">
                <CheckCircle className="w-8 h-8 text-[var(--accent-primary)] mx-auto mb-3" />
                <p className="text-[var(--text-body)]">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-2 text-center mb-12">Outcomes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              'Faster interventions with clearer visibility',
              'Better consistency in field execution',
              'Reporting that improves planning and accountability'
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-6 bg-[var(--bg-card)] border border-[var(--border-light)] rounded-lg">
                <CheckCircle className="w-6 h-6 text-[var(--accent-primary)] mb-3" />
                <p className="text-[var(--text-body)]">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="heading-2 mb-6">Bring clarity to the field—without adding complexity.</h2>
          <Link to="/contact" className="btn-primary">Talk to Ferntree Agriculture</Link>
        </div>
      </section>

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        title="Agriculture Intelligence Demo"
      />
    </div>
  );
};

export default Agriculture;